import { log } from '@/log'
import GameCampaignContents from '@/models/game/game-campaign-contents'
import GameParameterInterface from '@/models/game/parameters/interface'
import { GameType, Property } from '@/models/game/property'
import { Prize } from '@/models/game/response/complete'
import PlayHistory from '@/models/game/response/play-history'
import PlayInfo from '@/models/game/response/play-info'
import CampaignService from '@/services/api/campaign'
import GameService from '@/services/api/game'
import BalloonGameExecutor from '@/services/game/executor/balloon-executor'
import BarrelGameExecutor from '@/services/game/executor/barrel-executor'
import CraneGameExecutor from '@/services/game/executor/crane-executor'
import GameExecutorInterface from '@/services/game/executor/executor-interface'
import GachaExecutor from '@/services/game/executor/gacha-executor'
import GamelessExecutor from '@/services/game/executor/gameless-executor'
import JankenGameExecutor from '@/services/game/executor/janken-executor'
import MemoryExecutor from '@/services/game/executor/memory-executor'
import MovieGachaExecutor from '@/services/game/executor/movie-gacha-executor'
import RouletteGameExecutor from '@/services/game/executor/roulette-executor'
import ScratchGameExecutor from '@/services/game/executor/scratch-executor'
import SelectCardGameExecutor from '@/services/game/executor/select-card-executor'
import SlotGameExecutor from '@/services/game/executor/slot-executor'
import TurningLotteryGameExecutor from '@/services/game/executor/turning-lottery-executor'
import WaniPanicGameExecutor from '@/services/game/executor/wani-panic-executor'
import WhackMoleExecutor from '@/services/game/executor/whack-mole-executor'
import PresentCatchExecutor from './executor/present-catch-executor'

export default class GameProcessor {

  /**
   * FIXME:
   * 非ブランド配下の場合、brandEnglishNameは空文字 `''` を渡されることを期待している
   * これは明示的ではないので、nullableにするまたはOptionにするなど変更したい
   */
  static build(
    brandEnglishName: string,
    campaignId: string,
    gameOrganizer: string
  ): GameProcessor {

    return new GameProcessor(
      new CampaignService(),
      new GameService(),
      brandEnglishName,
      campaignId,
      gameOrganizer
    )
  }

  private playInfo: PlayInfo
  private gameCampaignContents: GameCampaignContents
  private executor: GameExecutorInterface

  constructor(
    private campaignService: CampaignService,
    private gameService: GameService,
    private brandEnglishName: string,
    private campaignId: string,
    private _gameOrganizer: string
  ) {}

  async execute(): Promise<void> {

    try {

      this.gameCampaignContents = await this.getGameCampaignContents()
      this.playInfo = this.gameCampaignContents.playInfo

      const gameProperty = this.gameCampaignContents.gameProperty
      const gameParameter = this.gameCampaignContents.gameParameter

      /**
       * ゲーム外のページで景品画像を表示したいという要求が出てきたため、一時的に手続きによる遷移先URLの制御を行う。
       * 画面遷移の仕様は1ゲーム1URLの方向に倒れていたため、現実装だと他ゲームへ極力影響を与えずに差し込む箇所が見つからなかった。
       * ゲーム起動処理はモデルを整理してリファクタが必要なため、そのタイミングで合わせて検討する。
       * https://github.com/my-color/front/issues/1373
       */
      if (this.playStatusIsCompleted) {

        if (this.isGameOver) {
          location.href = this.gameOverUrl

          return
        }

        location.href = this.makeGameResultUrl()

        return
      }

      if (!this.canExecute(gameProperty, this.playInfo)) {

        // TODO: エラー画面がない
        log.error(`can not execute game.`)

        location.href = this.topUrlWithBrandNameContext

        return
      }

      this.executor = this.createExecutor(gameProperty, gameParameter)
      await this.executor.execute()

    } catch (e) {

      log.error(e)
      location.href = this.topUrlWithBrandNameContext

      return
    }
  }

  async notifyGameStart(data: object): Promise<void> {

    const eitherStartResult = await this.gameService.postStart({
      ...data,
      id: this.campaignId,
    })

    eitherStartResult.mapLeft((error) => {

      log.error(error)

      location.href = this.topUrlWithBrandNameContext
    })
  }

  async notifyGameComplete(data: object = {}): Promise<void> {

    const eitherCompleteResult = await this.gameService.postComplete(
      {
        ...data,
        id: this.campaignId,
      },
      this.brandEnglishName
    )

    eitherCompleteResult.mapLeft((error) => {

      log.error(error)

      location.href = this.topUrlWithBrandNameContext
    })
  }

  async notifyGameCompleteWithScore(data: object = {}): Promise<Prize> {

    const eitherCompleteResult = await this.gameService.postCompleteScoreGame(
      {
        ...data,
        id: this.campaignId,
      },
      this.brandEnglishName
    )

    if (eitherCompleteResult.isLeft()) {

      throw this.createErrorObject('failed to complete with score.', this.topUrlWithBrandNameContext)
    }

    if (eitherCompleteResult.value.prize === null) {

      throw this.createErrorObject('no prize field.', this.topUrlWithBrandNameContext)
    }

    return eitherCompleteResult.value.prize
  }

  makeGameResultUrl() {
    if (this.brandEnglishName === '') {
      return `/game/${this.campaignId}/result`
    }

    return `/${this.brandEnglishName}/game/${this.campaignId}/result`
  }

  private createExecutor(
    gameProperty: Property,
    gameParameter: GameParameterInterface
  ): GameExecutorInterface {

    // 新規ゲームを追加する際などは https://my-color.esa.io/posts/981 を参照
    switch (gameProperty.type) {

      case GameType.Janken:
        return new JankenGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Scratch:
        return new ScratchGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Roulette:
        return new RouletteGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.SelectCard:
        return new SelectCardGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Gameless:
        return new GamelessExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Barrel:
        return new BarrelGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.WhackMole:
        return new WhackMoleExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Balloon:
        return new BalloonGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Gacha:
        return new GachaExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.WaniPanic:
        return new WaniPanicGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Slot:
        return new SlotGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.MovieGacha:
        return new MovieGachaExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.TurningLottery:
        return new TurningLotteryGameExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.Crane:
        return new CraneGameExecutor(
          this,
          gameProperty,
          gameParameter,
          this.gameOverUrl
        )

      case GameType.Memory:
        return new MemoryExecutor(
          this,
          gameProperty,
          gameParameter
        )

      case GameType.PresentCatch:
        return new PresentCatchExecutor(
          this,
          gameProperty,
          gameParameter
        )

      default:
        break
    }

    throw this.createErrorObject('executor is not created.', '/')
  }

  private createErrorObject(message: string, url: string): object {

    return {
      message,
      transitionUrl: url,
    }
  }

  private async getGameCampaignContents(): Promise<GameCampaignContents> {

    const eitherGameCampaignContents = await this.campaignService.post(this.campaignId, this.brandEnglishName)

    if (eitherGameCampaignContents.isLeft()) {

      throw this.createErrorObject('campaign is not found.', '/')
    }

    return eitherGameCampaignContents.value
  }

  // TODO 画面遷移によっては実行できない理由を返却する必要があるかもしれない
  private canExecute(gameProperty: Property, playInfo: PlayInfo): boolean {

    if (!this.validatePlayRequirements(gameProperty)) {
      return false
    }

    if (!this.validatePlayLimit(gameProperty, playInfo.playHistory)) {
      return false
    }

    return true
  }

  private validatePlayRequirements(gameProperty: Property): boolean {
    return gameProperty.playRequirements
  }

  private validatePlayLimit(gameProperty: Property, playHistory: PlayHistory): boolean {
    return playHistory.playedCount <= gameProperty.playLimit
  }

  get playStatusIsStarted(): boolean {
    return this.playInfo.playStatus.isStarted
  }

  get playStatusIsCompleted(): boolean {
    return this.playInfo.playStatus.isCompleted
  }

  get isGameOver(): boolean {
    return this.playInfo.isGameOver
  }

   // src/Controller/GameResultController.php の GAME_OVER_PAGE_NAME と同じにする
  get gameOverUrl(): string {
    return `${this.topUrlWithBrandNameContext}/game-over`
  }

  /**
   * ゲームのトップURLはゲーム開催元のブランドによって異なる
   * 開催元がホストだった場合、サイトのトップURLとなる。
   * 末尾に / は含まない
   * @returns {string}
   */
  get topUrlWithBrandNameContext(): string {
    const base = location.origin

    if (this.brandEnglishName === '') {
      return base
    }

    return `${base}/${this.brandEnglishName}`
  }

  get gameOrganizer(): string {
    return this._gameOrganizer
  }
}
